'use client';

/**
 * @file     useAppLanguage.ts
 * @readme   ./CLAUDE.md
 * @purpose  Hook that exposes the current app language, the supported language list, and a setLanguage function that deferred-loads the bundle and switches the i18n instance.
 */
import { useTranslation } from 'react-i18next';
import { defaultLanguage, supportedLanguages, type AppLanguage } from './languages';

const normalizeLanguage = (value: string | undefined): AppLanguage => {
  if (!value) {
    return defaultLanguage;
  }

  const normalized = value.split('-')[0] as AppLanguage;
  return supportedLanguages.includes(normalized) ? normalized : defaultLanguage;
};

export const useAppLanguage = () => {
  const { i18n } = useTranslation();

  const currentLanguage = normalizeLanguage(i18n.resolvedLanguage);

  const setLanguage = async (language: AppLanguage) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('relta-lang', language);
    }
    const { ensureLanguageBundle } = await import('./index');
    await ensureLanguageBundle(language);
    await i18n.changeLanguage(language);
  };

  return {
    currentLanguage,
    languages: supportedLanguages,
    setLanguage
  };
};
